import React, { useState } from 'react';
import { KatButton } from '@amzn/katal-react';
import { BatcherService, FunctionMetadata } from '../../services/Batcher/BatcherService';
import './BatcherPage.scss';

interface BatcherPageProps {
    stage: string;
    realm: string;
}

const BatcherPage: React.FC<BatcherPageProps> = ({ stage, realm }) => {
    // Functions the user has added to the chain
    const [chain, setChain] = useState<FunctionMetadata[]>([]);

    // Available functions fetched from the ListFunctions API
    const [availableFunctions, setAvailableFunctions] = useState<FunctionMetadata[]>([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleAddFunction = async () => {
        // Toggle the picker closed if already open
        if (pickerOpen) {
            setPickerOpen(false);
            return;
        }

        setError('');
        setLoading(true);
        try {
            const functions = await BatcherService.listFunctions(stage, realm);
            setAvailableFunctions(Array.isArray(functions) ? functions : []);
            setPickerOpen(true);
        } catch (e: any) {
            setError(e.message || 'Failed to load functions');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectFunction = (fn: FunctionMetadata) => {
        setChain(prev => [...prev, fn]);
        setPickerOpen(false);
    };

    const handleRemoveFunction = (index: number) => {
        setChain(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="batcher-page">
            <div className="batcher-header">
                <h1 className="batcher-title">Select Functions</h1>
                <p className="batcher-subtitle">Select functions and run them as a chain.</p>
            </div>

            <div className="batcher-panel">
                <div className="batcher-panel-header">
                    <span className="batcher-panel-title">Functions</span>
                    <KatButton
                        variant="secondary"
                        onClick={handleAddFunction}
                        disabled={loading}
                    >
                        {loading ? 'Loading...' : '+ Add a function to chain'}
                    </KatButton>
                </div>

                {error && <div className="batcher-error">{error}</div>}

                {pickerOpen && (
                    <div className="batcher-picker">
                        {availableFunctions.length === 0 ? (
                            <div className="batcher-picker-empty">No functions available</div>
                        ) : (
                            availableFunctions.map(fn => (
                                <button
                                    key={fn.functionIdentifier}
                                    type="button"
                                    className="batcher-picker-item"
                                    onClick={() => handleSelectFunction(fn)}
                                >
                                    <span className="batcher-picker-item-name">{fn.functionName}</span>
                                    {fn.aiContext && (
                                        <span className="batcher-picker-item-desc">{fn.aiContext}</span>
                                    )}
                                </button>
                            ))
                        )}
                    </div>
                )}

                {chain.length === 0 ? (
                    <div className="batcher-empty">No functions added</div>
                ) : (
                    <ol className="batcher-chain">
                        {chain.map((fn, index) => (
                            <li key={`${fn.functionIdentifier}-${index}`} className="batcher-chain-item">
                                <div className="batcher-chain-item-info">
                                    <span className="batcher-chain-item-name">{fn.functionName}</span>
                                    {fn.aiContext && (
                                        <span className="batcher-chain-item-desc">{fn.aiContext}</span>
                                    )}
                                </div>
                                <KatButton
                                    variant="link"
                                    onClick={() => handleRemoveFunction(index)}
                                >
                                    Remove
                                </KatButton>
                            </li>
                        ))}
                    </ol>
                )}
            </div>
        </div>
    );
};

export default BatcherPage;
